import { createWebHistory, createRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import Cookies from 'js-cookie';
import adminRoutes from './AdminRoutes';
import authRoutes from './authRoutes';
import store from '@/vuex/store';
import { setItem } from '@/utility/localStorageControl';
import { usePermissions } from '@/composables/usePermissions';

const routes = [{
        name: 'Manage',
        path: '/manage',
        component: () =>
            import ( /* webpackChunkName: "manage" */ '@/layout/withManageLayout.vue'),
        children: [{
            path: '',
            name: 'manage',
            component: () =>
                import ( /* webpackChunkName: "manage" */ '@/view/pages/Manage.vue'),
        }, ],
        meta: { auth: false },
    },
    {
        name: 'Admin',
        path: '/',
        component: () =>
            import ( /* webpackChunkName: "admin" */ '@/layout/withAdminLayout.vue'),
        children: [...adminRoutes],
        meta: { auth: false },
    },
    {
        name: 'Auth',
        path: '/auth',
        component: () =>
            import ( /* webpackChunkName: "auth" */ '@/layout/withAuthLayout.vue'),
        children: [...authRoutes],
        meta: { auth: true },
    },
    {
        name: 'UrlRedirect',
        path: '/r/:shortCode',
        component: () =>
            import ( /* webpackChunkName: "url-redirect" */ '@/view/pages/UrlRedirect.vue'),
        meta: { auth: false, public: true },
    },
];

const router = createRouter({
    history: createWebHistory(
        process.env.NODE_ENV === 'production' ?
        process.env.VUE_APP_SUB_ROUTE ?
        process.env.VUE_APP_SUB_ROUTE :
        process.env.BASE_URL :
        process.env.BASE_URL,
    ),
    linkExactActiveClass: 'active',
    routes,
});

router.beforeEach(async (to, from, next) => {
    // === HubSpot OAuth Callback ===
    // Backend redirects here after user installs app from HubSpot Marketplace
    // URL: ?oauth=success&portal=XXXXX&token=JWT_TOKEN
    if (to.query.oauth === 'success' && to.query.token) {
        const token = to.query.token;
        const portal = to.query.portal;

        // Store JWT token (same keys as normal login flow)
        setItem('x-token', token);
        setItem('x_token', token);

        // Store HubSpot portal ID
        if (portal) {
            setItem('hubspot_portal_id', portal);
        }

        // Set auth cookie + Vuex state (same as dispatch('login'))
        Cookies.set('logedIn', true);
        store.state.auth.login = true;

        console.log('OAuth callback: token saved, portal:', portal);

        // โหลด permissions ก่อน redirect
        const { loadPermissions: loadPerms } = usePermissions();
        await loadPerms(true);

        // Redirect to manage page with clean URL
        next({ path: '/manage', replace: true });
        return;
    }

    // Allow public routes without authentication
    if (to.meta.public) {
        next();
        return;
    }
    
    // console.log(to, store);
    if (to.meta.auth && store.state.auth.login) {
        next({ path: '/manage' });
    } else if (!to.meta.auth && !store.state.auth.login) {
        next({ name: 'login' });
    } else {
        // Permission System — ใช้ข้อมูลจาก API (GET /api/view-permissions)
        if (store.state.auth.login) {
            const { loadPermissions, hasPermission } = usePermissions();

            // โหลด permissions จาก API (ครั้งแรกเท่านั้น, มี cache)
            await loadPermissions();

            // เช็คว่า route เป็น platform path ที่ต้องการ permission หรือไม่
            const platformPaths = [
                { prefix: '/flowaccount', permission: 'flowaccount' },
                { prefix: '/peakaccount', permission: 'peakaccount' },
                { prefix: '/thaibulksms', permission: 'thaibulksms' },
                { prefix: '/smartcomm', permission: 'smartcomm' },
                { prefix: '/marketplace', permission: 'marketplace' },
            ];

            const matchedPlatform = platformPaths.find(p => to.path.startsWith(p.prefix));

            if (matchedPlatform && !hasPermission(matchedPlatform.permission)) {
                // ไม่มีสิทธิ์เข้าถึงแพลตฟอร์มนี้
                message.warning('คุณไม่มีสิทธิ์เข้าถึงส่วนนี้', 5);
                next({ path: '/manage' });
            } else {
                next();
            }
        } else {
            next();
        }
    }
    window.scrollTo(0, 0); // reset scroll position to top of page
});

export default router;